import { useLtcPlanComparison } from '@client/hooks/useLtcPlanComparison';
import { formatCurrency } from '@shared';
import { AlertTriangle, CheckCircle } from 'lucide-react';

import { StepContentProps } from '../types';

export function CoverageAssessmentStep(props: StepContentProps) {
  const {
    yearsTillLtcRounded,
    projectedLtcCost,
    projectedLtcCostWithoutInflation,
    selfFundingCapacity,
    coverageGap,
    coverageSurplus,
    isWellPositioned,
    selectedAssets,
  } = useLtcPlanComparison();

  return (
    <div className="flex flex-col items-center py-8">
      <div
        className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl ${
          isWellPositioned ? 'bg-primary text-white' : 'bg-amber-100 text-amber-600'
        }`}
      >
        {isWellPositioned ? (
          <CheckCircle className="h-8 w-8" />
        ) : (
          <AlertTriangle className="h-8 w-8" />
        )}
      </div>

      <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Your Coverage Assessment</h1>

      <div className="mx-auto mt-8 grid w-full max-w-2xl grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-6">
          <p className="text-sm font-medium text-gray-700">Self-Funding Capacity</p>
          <p className="text-3xl font-bold text-green-500">{formatCurrency(selfFundingCapacity)}</p>
          <p className="text-sm text-gray-500">
            {yearsTillLtcRounded}-year projection based on your assets
          </p>
        </div>

        <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-6">
          <p className="text-sm font-medium text-gray-700">Projected LTC Cost</p>
          <p className="text-3xl font-bold text-rose-500">{formatCurrency(projectedLtcCost)}</p>
          <p className="text-sm text-gray-500">
            In Today's Dollars: {formatCurrency(projectedLtcCostWithoutInflation)}
          </p>
        </div>
      </div>

      {/* Visual Progress Bar */}
      <div className="mx-auto mt-8 w-full max-w-2xl">
        <div className="flex items-end justify-between">
          <div className="flex items-end gap-3">
            <span className="text-2xl font-semibold text-gray-900">
              {Math.min(100, Math.round((selfFundingCapacity / projectedLtcCost) * 100))}%
            </span>
            <span className="text-gray-700">
              toward goal of{' '}
              <span className="text-gray-900">{formatCurrency(projectedLtcCost)}</span>
            </span>
          </div>
          <div className="flex gap-8 text-right">
            <div>
              <p className="text-sm font-medium text-gray-700">Projected Coverage</p>
              <p className="text-lg font-bold text-green-500">
                {formatCurrency(selfFundingCapacity)}
              </p>
            </div>
            {coverageGap > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700">Coverage Gap</p>
                <p className="text-lg font-bold text-amber-500">{formatCurrency(coverageGap)}</p>
              </div>
            )}
            {coverageSurplus > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-500">Surplus</p>
                <p className="text-lg font-bold text-amber-500">
                  +{formatCurrency(coverageSurplus)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="bg-primary h-full rounded-full transition-all duration-500"
            style={{ width: `${Math.min(100, (selfFundingCapacity / projectedLtcCost) * 100)}%` }}
          />
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Coverage calculated from funding sources in your plan
        </p>
      </div>

      <div
        className={`mx-auto mt-6 w-full max-w-2xl rounded-xl p-6 text-center ${
          isWellPositioned ? 'bg-primary/5' : 'bg-amber-50'
        }`}
      >
        {isWellPositioned ? (
          <>
            <h3 className="text-lg font-semibold text-gray-900">
              Great news! You're well-positioned
            </h3>
            <p className="mt-2 text-gray-700">
              We're going to protect your assets and save you money by finding financial vehicles
              that could be more efficient in a long-term care event.
            </p>
            {coverageSurplus > 0 && (
              <p className="text-primary mt-3 text-sm">
                You have a projected surplus of{' '}
                <span className="font-semibold">{formatCurrency(coverageSurplus)}</span> above
                estimated LTC costs.
              </p>
            )}
          </>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-gray-900">You have a coverage gap</h3>
            <p className="mt-2 text-gray-700">
              Based on your current assets, you may need additional coverage to fully protect
              yourself from long-term care costs.
            </p>
            <p className="mt-3 text-sm text-amber-700">
              Estimated gap: <span className="font-semibold">{formatCurrency(coverageGap)}</span>
            </p>
          </>
        )}
      </div>

      <div className="mx-auto mt-8 w-full max-w-2xl">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <h4 className="font-semibold text-gray-900">How we calculated this</h4>
          <ul className="mt-3 list-inside list-disc space-y-2.5 text-sm text-gray-600">
            <li>
              Projected your {selectedAssets.length} account(s) forward {yearsTillLtcRounded} years
              using your expected returns
            </li>
            <li>Applied gains taxes to your investment growth</li>
            <li>Included compounded monthly growth on future contributions</li>
            <li>Compared against your estimated long-term care costs</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
